import { createSlice, PayloadAction, current } from '@reduxjs/toolkit';

// types.
import { ContextMenuType, ContextMenuItem } from './context-menu';

//  --------------------------------------------------------------------------
//
//  C O N S T A N T S
//
//  --------------------------------------------------------------------------

//  define the div elements that can hold context menus, and add the
//  div ids.
export enum CONTEXT_CONTAINERS {
  NONE = -1,
  PROJECT,
  FILE_BROWSER,
  SEARCH_CATALOG_SELECTED_ITEMS,
  FILE_BROWSER_WHOLE_WINDOW,
  SEARCH_CATALOG_WHOLE_WINDOW,
}
const NUM_CONTEXT_CONTAINERS: number = Object.keys(CONTEXT_CONTAINERS).length - 1;
export const CONTAINER_IDS: string[] = [
  'project-container',
  'file-browser-container',
  'data-item-container',
  'file-browser-whole-window',
  'search-catalog-whole-window',
];

// define the list of unique context-menu systems.
export enum CONTEXT_SYSTEMS {
  FILE_BROWSER = 0,
  SEARCH_CATALOG_FORM,
  SEARCH_CATALOG_SELECTED_ITEMS,
  PROJECT_DATA_FILES,
  SEARCH_CATALOG_FORM_CAOM,
  FILE_BROWSER_STAGING,
} // CONTEXT_SYSTEMS

// define the number of context menus in each system, the width of the menus, and the container
// that the menu sits in.
export const NUM_CONTEXT_MENUS: number[] = [3, 1, 3, 3, 1, 3];
export const CONTEXT_MENU_WIDTH: number[] = [250, 250, 250, 250, 250, 250];
export const CONTEXT_SYSTEM_CONTAINERS: CONTEXT_CONTAINERS[] = [
  CONTEXT_CONTAINERS.FILE_BROWSER,
  CONTEXT_CONTAINERS.SEARCH_CATALOG_WHOLE_WINDOW,
  CONTEXT_CONTAINERS.SEARCH_CATALOG_WHOLE_WINDOW, //SEARCH_CATALOG_SELECTED_ITEMS,
  CONTEXT_CONTAINERS.PROJECT,
  CONTEXT_CONTAINERS.NONE,
  CONTEXT_CONTAINERS.FILE_BROWSER_WHOLE_WINDOW,
]; // CONTEXT_SYSTEM_CONTAINERS

// define the menu IDs for each context system.
export enum CONTEXT_MENU_FILE_BROWSER {
  NONE = -1,
  MAIN,
  SITES,
  STORAGES,
} // CONTEXT_MENU_FILE_BROWSER

export enum CONTEXT_MENU_SEARCH_CATALOG_FORM {
  NONE = -1,
  METADATA_LIST,
} // CONTEXT_MENU_SEARCH_CATALOG_FORM

export enum CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS {
  NONE = -1,
  MAIN,
  SITES,
  STORAGES,
} // CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS

export enum CONTEXT_MENU_DATA_STAGING {
  NONE = -1,
  MAIN,
  SITES,
  STORAGES,
} // CONTEXT_MENU_DATA_STAGING

export enum CONTEXT_MENU_SEARCH_CATALOG_FORM_CAOM {
  NONE = -1,
  METADATA_LIST,
} // CONTEXT_MENU_SEARCH_CATALOG_FORM_CAOM

export enum CONTEXT_MENU_FILE_BROWSER_STAGING {
  NONE = -1,
  MAIN,
  SITES,
  STORAGES,
} // CONTEXT_MENU_FILE_BROWSER_STAGING

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

export interface ContextMenuState {
  size: {
    x: number;
    y: number;
  }[];
  scrollPosition: number[];
  contextVisible: boolean[][];
  contextPos: { x: number; y: number }[][];
  contextRow: number[][];
  contextMenuDefinition: ContextMenuType[][];
}

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

const initialState: ContextMenuState = {
  //
  size: Array(NUM_CONTEXT_CONTAINERS).fill({
    x: 0.0,
    y: 0.0,
  }),
  scrollPosition: Array(NUM_CONTEXT_CONTAINERS).fill(0.0),
  contextVisible: NUM_CONTEXT_MENUS.map((item: number) => {
    return Array(item).fill(false);
  }),
  contextPos: NUM_CONTEXT_MENUS.map((item: number) => {
    return Array(item).fill({
      x: 0.0,
      y: 0.0,
    });
  }),
  contextRow: NUM_CONTEXT_MENUS.map((item: number) => {
    return Array(item).fill(-1);
  }),
  contextMenuDefinition: [
    //
    // file browser:
    [
      //
      // main context menu.
      {
        items: [],
        parentMenu: CONTEXT_MENU_FILE_BROWSER.NONE,
        parentRowID: -1,
      },

      // list of sites.
      {
        items: [],
        parentMenu: CONTEXT_MENU_FILE_BROWSER.MAIN,
        parentRowID: -1,
      },

      // list of storage areas.
      {
        items: [],
        parentMenu: CONTEXT_MENU_FILE_BROWSER.SITES,
        parentRowID: -1,
      },
      //
    ],

    // search-catalog form:
    [
      //
      // list of metadata filters.
      {
        items: [],
        parentMenu: CONTEXT_MENU_SEARCH_CATALOG_FORM.NONE,
        parentRowID: -1,
      },
      //
    ],

    // search-catalog selected items:
    [
      //
      // main context menu.
      {
        items: [],
        parentMenu: CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.NONE,
        parentRowID: -1,
      },

      // list of sites.
      {
        items: [],
        parentMenu: CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.MAIN,
        parentRowID: -1,
      },

      // list of storage areas.
      {
        items: [],
        parentMenu: CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS.SITES,
        parentRowID: -1,
      },
      //
    ],

    // project-data files:
    [
      //
      // main context menu.
      {
        items: [],
        parentMenu: CONTEXT_MENU_DATA_STAGING.NONE,
        parentRowID: -1,
      },

      // list of sites.
      {
        items: [],
        parentMenu: CONTEXT_MENU_DATA_STAGING.MAIN,
        parentRowID: -1,
      },

      // list of storage areas.
      {
        items: [],
        parentMenu: CONTEXT_MENU_DATA_STAGING.SITES,
        parentRowID: -1,
      },
      //
    ],

    // search-catalog form (CAOM):
    [
      //
      // list of metadata filters.
      {
        items: [],
        parentMenu: CONTEXT_MENU_SEARCH_CATALOG_FORM_CAOM.NONE,
        parentRowID: -1,
      },
      //
    ],

    // file-browser-data files:
    [
      //
      // main context menu.
      {
        items: [],
        parentMenu: CONTEXT_MENU_FILE_BROWSER_STAGING.NONE,
        parentRowID: -1,
      },

      // list of sites.
      {
        items: [],
        parentMenu: CONTEXT_MENU_FILE_BROWSER_STAGING.MAIN,
        parentRowID: -1,
      },

      // list of storage areas.
      {
        items: [],
        parentMenu: CONTEXT_MENU_FILE_BROWSER_STAGING.SITES,
        parentRowID: -1,
      },
      //
    ],
    //
  ],
  //
};

//	--------------------------------------------------------------------------
//
//	S L I C E S
//
//	--------------------------------------------------------------------------

export const contextMenuSlice = createSlice({
  name: 'contextMenu',
  initialState: initialState,
  reducers: {
    //
    ctx_setContextMenuDefinition: (
      state: ContextMenuState,
      action: PayloadAction<{
        contextSystem: CONTEXT_SYSTEMS;
        contextMenu: number;
        items: ContextMenuItem[];
        parentMenu?: number;
        parentRowID?: number;
      }>,
    ) => {
      //
      let currentState: ContextMenuState = current(state);

      // the user can either supply a context-menu index, and then only the first item in the array
      // is used, or the user can leave the index undefined, and supply ALL the context definitions.
      state.contextMenuDefinition = currentState.contextMenuDefinition.map(
        (system, sysIndex) => {
          //
          if (sysIndex === action.payload.contextSystem) {
            return system.map((menu, mnuIndex) => {
              //
              if (mnuIndex === action.payload.contextMenu) {
                //
                let newMenu: ContextMenuType = Object.assign({}, menu);
                newMenu.items = action.payload.items;
                if (action.payload.parentMenu !== undefined) {
                  newMenu.parentMenu = action.payload.parentMenu;
                }
                if (action.payload.parentRowID !== undefined) {
                  newMenu.parentRowID = action.payload.parentRowID;
                }
                return newMenu;
                //
              } else {
                return menu;
              }
              //
            });
          } else {
            return system;
          }
          //
        },
      );
      //
    },

    ctx_setContextPos: (
      state: ContextMenuState,
      action: PayloadAction<{
        contextSystem: CONTEXT_SYSTEMS;
        contextMenu?: number;
        pos: {
          x: number;
          y: number;
        };
      }>,
    ) => {
      //
      state.contextPos = state.contextPos.map((system, sysIndex) => {
        //
        if (sysIndex === action.payload.contextSystem) {
          return state.contextPos[sysIndex].map((menu, mnuIndex) => {
            //
            if (
              mnuIndex === action.payload.contextMenu ||
              action.payload.contextMenu === undefined
            ) {
              return action.payload.pos;
            } else {
              return menu;
            }
            //
          });
        } else {
          return system;
        }
        //
      });
      //
    },

    ctx_setContextRow: (
      state: ContextMenuState,
      action: PayloadAction<{
        contextSystem: CONTEXT_SYSTEMS;
        contextMenu?: number;
        row: number;
      }>,
    ) => {
      //
      state.contextRow = state.contextRow.map((system, sysIndex) => {
        //
        if (sysIndex === action.payload.contextSystem) {
          return state.contextRow[sysIndex].map((menu, mnuIndex) => {
            //
            if (
              mnuIndex === action.payload.contextMenu ||
              action.payload.contextMenu === undefined
            ) {
              return action.payload.row;
            } else {
              return menu;
            }
            //
          });
        } else {
          return system;
        }
        //
      });
      //
    },

    ctx_setContextVisible: (
      state: ContextMenuState,
      action: PayloadAction<{
        contextSystem: CONTEXT_SYSTEMS;
        contextMenu?: number;
        visible: boolean;
      }>,
    ) => {
      //
      state.contextVisible = state.contextVisible.map((system, sysIndex) => {
        //
        if (sysIndex === action.payload.contextSystem) {
          return state.contextVisible[sysIndex].map((menu, mnuIndex) => {
            //
            if (
              mnuIndex === action.payload.contextMenu ||
              action.payload.contextMenu === undefined
            ) {
              return action.payload.visible;
            } else {
              //
              // hide all lower-level menus on this context system.
              if (
                mnuIndex > action.payload.contextMenu &&
                action.payload.visible === true
              ) {
                return false;
              } else {
                return menu;
              }
              //
            }
            //
          });
        } else {
          if (action.payload.visible === true) {
            return Array(NUM_CONTEXT_MENUS[sysIndex]).fill(false);
          } else {
            return system;
          }
        }
        //
      });

      // de-select all rows in other context systems.
      if (action.payload.visible === true) {
        //
        state.contextRow = state.contextRow.map((system, sysIndex) => {
          //
          if (sysIndex !== action.payload.contextSystem) {
            return Array(NUM_CONTEXT_MENUS[sysIndex]).fill(-1);
          } else {
            return system;
          }
          //
        });
        //
      }
      //
    },

    ctx_setScrollPosition: (
      state: ContextMenuState,
      action: PayloadAction<{
        container: CONTEXT_CONTAINERS;
        scrollPosition: number;
      }>,
    ) => {
      //
      state.scrollPosition = state.scrollPosition.map((item, index) => {
        //
        if (index === action.payload.container) {
          return action.payload.scrollPosition;
        } else {
          return item;
        }
        //
      });
      //
    },

    ctx_setSize: (
      state: ContextMenuState,
      action: PayloadAction<{
        container: CONTEXT_CONTAINERS;
        size: {
          x: number;
          y: number;
        };
      }>,
    ) => {
      //
      state.size = state.size.map((item, index) => {
        //
        if (index === action.payload.container) {
          return action.payload.size;
        } else {
          return item;
        }
        //
      });
      //
    },
    //
  },
});

// expose the interface.
export const {
  ctx_setContextMenuDefinition,
  ctx_setContextPos,
  ctx_setContextRow,
  ctx_setContextVisible,
  ctx_setScrollPosition,
  ctx_setSize,
} = contextMenuSlice.actions;
export default contextMenuSlice.reducer;
